/*
 * Cart reward goals controller (birddogs-style).
 *
 * Works WITH the Be Yours mini-cart. The whole `mini-cart` section re-renders
 * server-side on every cart change, so the progress bar, goal states and the gift
 * pool markup are produced by Liquid (snippets/cart-rewards-bar.liquid +
 * snippets/cart-gift-pool.liquid). This script only:
 *   1. Adds a chosen gift to the cart (with line properties _free_gift + _reward_goal).
 *   2. Opens the variant ("choose your size") popover for multi-variant gifts.
 *   3. Reconciles after every cart change ("rewind"): auto-removes any gift whose
 *      goal is no longer met, or that exceeds the goal's "choose N" limit.
 *
 * Making the gift $0 is done by a Shopify automatic discount (100% off the gift
 * products). This script never sets prices.
 */
(function () {
  if (window.__cartRewardsInit) return;
  window.__cartRewardsInit = true;

  const GIFT_PROP = '_free_gift';
  const GOAL_PROP = '_reward_goal';

  let reconciling = false;

  function rootUrl() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function sectionIds() {
    const miniCart = document.querySelector('mini-cart');
    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      return miniCart.getSectionsToRender().map((s) => s.id);
    }
    return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  }

  function readConfig() {
    const el = document.querySelector('cart-rewards [data-cart-rewards-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // --- Add a gift -----------------------------------------------------------
  function addGift(variantId, goalIndex, triggerEl) {
    if (!variantId) return;
    if (triggerEl) triggerEl.classList.add('loading');

    const miniCart = document.querySelector('mini-cart');
    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      properties: { [GIFT_PROP]: 'true', [GOAL_PROP]: String(goalIndex) },
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });

    fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status === 422) {
          document.dispatchEvent(
            new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } })
          );
          return;
        }
        // Re-render the drawer (keeps it open), then reconcile to enforce limits.
        if (miniCart && typeof miniCart.renderContents === 'function') {
          miniCart.renderContents(parsedState);
        }
        reconcile();
      })
      .catch((e) => console.error('[cart-rewards] add failed', e))
      .finally(() => {
        if (triggerEl) triggerEl.classList.remove('loading');
      });
  }

  // --- Change / remove a single line ---------------------------------------
  function changeLine(idOrKey, quantity) {
    const body = JSON.stringify({
      id: idOrKey,
      quantity: quantity,
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });

    return fetch(`${theme.routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-rewards', cart: parsedState });
        return parsedState;
      });
  }

  // Remove several lines at once (one request, one re-render) — keeps rewind snappy.
  function removeLines(keys) {
    const updates = {};
    keys.forEach((k) => (updates[k] = 0));
    const body = JSON.stringify({
      updates: updates,
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });

    return fetch(`${theme.routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((parsedState) => {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-rewards', cart: parsedState });
        return parsedState;
      });
  }

  // --- Reconcile / rewind ---------------------------------------------------
  // Goals are measured on non-gift items only, so removing gifts never changes
  // goal state — every invalid gift can be removed in a single batched pass.
  async function reconcile(cartData) {
    if (reconciling) return;
    const config = readConfig();
    if (!config || !config.goals) return;

    reconciling = true;

    try {
      const cart =
        cartData && Array.isArray(cartData.items)
          ? cartData
          : await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      let total = 0;
      let qty = 0;
      const gifts = [];

      (cart.items || []).forEach((item) => {
        const props = item.properties || {};
        if (props[GIFT_PROP]) {
          gifts.push(item);
        } else {
          total += item.final_line_price;
          qty += item.quantity;
        }
      });

      const perGoal = {};
      const removeKeys = [];

      for (const gift of gifts) {
        const gi = String((gift.properties || {})[GOAL_PROP] || '');
        const goal = config.goals.find((g) => String(g.index) === gi);

        let remove = false;
        if (!goal || goal.rewardType !== 'gift') {
          remove = true;
        } else {
          const met = goal.type === 'amount' ? total >= goal.threshold : qty >= goal.threshold;
          if (!met) {
            remove = true;
          } else {
            perGoal[gi] = (perGoal[gi] || 0) + gift.quantity;
            if (perGoal[gi] > goal.choose) remove = true;
          }
        }
        if (remove) removeKeys.push(gift.key);
      }

      if (removeKeys.length) {
        await removeLines(removeKeys);
      }
    } catch (e) {
      console.error('[cart-rewards] reconcile failed', e);
    } finally {
      reconciling = false;
    }
  }

  // --- Variant popover ------------------------------------------------------
  // The gift cards live in a horizontally-scrolling row (overflow clips on both
  // axes), so an anchored popover gets cropped. Instead we mount the variant
  // picker as a centered overlay on <body>, where nothing can clip it.
  const OVERLAY_ID = 'cart-gift-variant-overlay';

  function closeVariantOverlay() {
    const ov = document.getElementById(OVERLAY_ID);
    if (ov) ov.remove();
    document
      .querySelectorAll('[data-gift-variant-toggle][aria-expanded="true"]')
      .forEach((b) => b.setAttribute('aria-expanded', 'false'));
  }

  function openVariantOverlay(sourcePopover) {
    closeVariantOverlay();
    const overlay = document.createElement('div');
    overlay.id = OVERLAY_ID;
    overlay.className = 'cart-gift-variant-overlay';

    const backdrop = document.createElement('div');
    backdrop.className = 'cart-gift-variant-overlay__backdrop';
    backdrop.setAttribute('data-gift-variant-close', '');

    const panel = document.createElement('div');
    panel.className = 'cart-gift-variant-overlay__panel';
    // Reuse the (hidden) source markup: title, close button, variant buttons.
    panel.innerHTML = sourcePopover.innerHTML;

    overlay.appendChild(backdrop);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
  }

  // --- Delegated events (survive section re-renders) ------------------------
  document.addEventListener('click', (event) => {
    const addBtn = event.target.closest('[data-gift-add]');
    if (addBtn) {
      event.preventDefault();
      if (addBtn.hasAttribute('disabled')) return;
      addGift(addBtn.dataset.variantId, addBtn.dataset.rewardGoal, addBtn);
      closeVariantOverlay();
      return;
    }

    const removeBtn = event.target.closest('[data-gift-remove]');
    if (removeBtn) {
      event.preventDefault();
      const card = removeBtn.closest('[data-line-key]');
      if (card && card.dataset.lineKey) {
        removeBtn.classList.add('loading');
        changeLine(card.dataset.lineKey, 0);
      }
      return;
    }

    const toggleBtn = event.target.closest('[data-gift-variant-toggle]');
    if (toggleBtn) {
      event.preventDefault();
      const alreadyOpen = toggleBtn.getAttribute('aria-expanded') === 'true';
      if (alreadyOpen) {
        closeVariantOverlay();
        return;
      }
      const popover = toggleBtn.parentElement.querySelector('.cart-gift-card__variants');
      if (popover) {
        openVariantOverlay(popover);
        toggleBtn.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    if (event.target.closest('[data-gift-variant-close]')) {
      event.preventDefault();
      closeVariantOverlay();
      return;
    }
  });

  // Close the variant overlay on Escape.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeVariantOverlay();
  });

  // Reconcile after every cart change (add/remove/qty from anywhere).
  // Reuse the cart payload from the event so we skip an extra /cart.js round-trip.
  document.addEventListener('cart:updated', (event) => {
    closeVariantOverlay();
    reconcile(event.detail && event.detail.cart);
  });

  // Reconcile once when the drawer content first loads.
  document.addEventListener('cartdrawer:opened', () => reconcile());
})();
